<template>
  <div class="area-setting-layer"></div>
</template>

<script>
import rgPlayBackService from "@/services/rgPlayBackService";

const SOURCE_IDS = {
  AREA_SETTING_POLYGON: "area-setting-polygon",
  AREA_SETTING_MASK: "area-setting-mask",
  AREA_SETTING_POINT: "area-setting-point",
};
const LAYER_IDS = {
  AREA_SETTING_POLYGON_LAYER: "area-setting-polygon-layer",
  AREA_SETTING_POLYGON_LINE_LAYER: "area-setting-polygon-line-layer",
  AREA_SETTING_POLYGON_MASK_LAYER: "area-setting-polygon-mask-layer",
  AREA_SETTING_POINT_LAYER: "area-setting-point-layer",
};

const EMIT_EVENTS = {
  UPDATE_AREA_SETTING: "update-area-setting",
};
const MAP_EVENTS = {
  MOUSE_MOVE: "mousemove",
  MOUSE_UP: "mouseup",
};

const LAYER_EVENTS = {
  [LAYER_IDS.AREA_SETTING_POINT_LAYER]: {
    MOUSE_MOVE: "mousemove",
    MOUSE_LEAVE: "mouseleave",
    MOUSE_DOWN: "mousedown",
  },
};

export default {
  name: "AreaSettingLayer",
  mixins: [],
  props: {
    map: Object,
    isReady: { type: Boolean, default: false },
    centerPointLngLat: {
      type: Object, // { lng: number, lat: number}
      default: null,
    },
    areaSettings: {
      type: Object, // : RgPlaybackSettings
      required: false,
    },
  },
  data() {
    return {
      initialized: false,
      farthestPoint: null,
      drag: false,
    };
  },
  computed: {
    areaPolygon() {
      if (!this.areaSettings) return [];
      return this.areaSettings.calcPolygon();
    },
    areaMask() {
      if (!this.areaPolygon) return [];
      return this.areaSettings.calcMask();
    },
    areaPoints() {
      if (!this.areaSettings) return [];
      return this.areaSettings.calcPoints();
    },
    areaSettingParams() {
      if (!this.areaSettings) return {};
      return this.areaSettings.calcSpecifyAreaSettingParams();
    },
  },
  watch: {
    isReady() {
      if (this.isReady) {
        if (!this.initialized) {
          this.initialize();
        }
      }
    },
    areaSettingParams: {
      handler() {
        this.setAreaSettingData();
      },
      depp: true,
    },
  },

  methods: {
    initialize() {
      this.setAreaSettingLayer();
      this.setAreaSettingData();
      this.initialized = true;
    },
    setAreaSettingData() {
      if (!this.map) return;
      if (!this.isReady) return;
      if (!this.areaPolygon) return;
      const { map } = this;
      map.getSource(SOURCE_IDS.AREA_SETTING_POLYGON).setData({
        type: "FeatureCollection",
        features: [this.areaPolygon],
      });
      map
        .getSource(SOURCE_IDS.AREA_SETTING_MASK)
        .setData({ type: "FeatureCollection", features: [this.areaMask] });
      map.getSource(SOURCE_IDS.AREA_SETTING_POINT).setData({
        type: "FeatureCollection",
        features: this.areaPoints,
      });
    },
    setAreaSettingLayer() {
      this.setAreaSettingPolygonLayer();
      this.setAreaSettingMaskLayer();
      this.setAreaSettingPointLayer();
    },
    setAreaSettingPolygonLayer() {
      if (!this.map) return;
      const { map } = this;

      map.addSource(SOURCE_IDS.AREA_SETTING_POLYGON, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      map.addLayer({
        id: LAYER_IDS.AREA_SETTING_POLYGON_LAYER,
        type: "fill",
        source: SOURCE_IDS.AREA_SETTING_POLYGON,
        layout: {},
        paint: {
          "fill-color": "#BDBDBD",
          "fill-opacity": 0.2,
        },
      });
      map.addLayer({
        id: LAYER_IDS.AREA_SETTING_POLYGON_LINE_LAYER,
        type: "line",
        source: SOURCE_IDS.AREA_SETTING_POLYGON,
        layout: {},
        paint: {
          "line-color": "#BDBDBD",
          "line-width": 1,
        },
      });
    },
    setAreaSettingMaskLayer() {
      const { map } = this;
      if (!map) return;
      map.addSource(SOURCE_IDS.AREA_SETTING_MASK, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      map.addLayer({
        id: LAYER_IDS.AREA_SETTING_POLYGON_MASK_LAYER,
        type: "fill",
        source: SOURCE_IDS.AREA_SETTING_MASK,
        layout: {},
        paint: {
          "fill-color": "#000000",
          "fill-opacity": 0.3,
        },
      });
    },
    setAreaSettingPointLayer() {
      if (!this.map) return;
      const { map } = this;

      map.addSource(SOURCE_IDS.AREA_SETTING_POINT, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      map.addLayer({
        id: LAYER_IDS.AREA_SETTING_POINT_LAYER,
        type: "circle",
        source: SOURCE_IDS.AREA_SETTING_POINT,
        paint: {
          "circle-radius": 10,
          "circle-opacity": 0,
        },
      });
      map.on(
        LAYER_EVENTS[LAYER_IDS.AREA_SETTING_POINT_LAYER].MOUSE_MOVE,
        LAYER_IDS.AREA_SETTING_POINT_LAYER,
        this.mouseMoveLayerHandler
      );
      map.on(
        LAYER_EVENTS[LAYER_IDS.AREA_SETTING_POINT_LAYER].MOUSE_LEAVE,
        LAYER_IDS.AREA_SETTING_POINT_LAYER,
        this.mouseLeaveLayerHandler
      );

      map.on(
        LAYER_EVENTS[LAYER_IDS.AREA_SETTING_POINT_LAYER].MOUSE_DOWN,
        LAYER_IDS.AREA_SETTING_POINT_LAYER,
        this.mouseDownLayerHandler
      );
    },

    mouseMoveLayerHandler() {
      this.setCursorMouseMove();
    },

    mouseLeaveLayerHandler() {
      if (this.drag) return;
      this.unsetCursorMouseMove();
    },

    mouseDownLayerHandler(e) {
      this.drag = true;
      this.map.dragPan.disable();
      this.farthestPoint = this.areaSettings.calcFarthestPoint(e.features[0]);
      this.map.on(MAP_EVENTS.MOUSE_MOVE, this.mouseMoveMapHandler);
      this.map.once(MAP_EVENTS.MOUSE_UP, this.mouseUpMapHandler);
    },

    mouseUpMapHandler() {
      this.map.off(MAP_EVENTS.MOUSE_MOVE, this.mouseMoveMapHandler);
      this.farthestPoint = null;
      this.map.dragPan.enable();
      this.unsetCursorDrag();
      this.drag = false;
    },

    mouseMoveMapHandler(e) {
      this.setCursorDrag();
      const tmpAreaSetting = new rgPlayBackService.createRgPlaybackSettingsFromCoordinates(
        [this.farthestPoint.geometry.coordinates, [e.lngLat.lng, e.lngLat.lat]],
        this.map.getZoom()
      );
      if (tmpAreaSetting.isAreaValid()) {
        this.$emit(EMIT_EVENTS.UPDATE_AREA_SETTING, {
          areaSetting: tmpAreaSetting.calcSpecifyAreaSettingParams(),
        });
      }
    },
    setCursorMouseMove() {
      this.map.getCanvas().style.cursor = "move";
    },
    unsetCursorMouseMove() {
      this.map.getCanvas().style.cursor = "";
    },
    setCursorDrag() {
      const { style } = document.documentElement;
      style.setProperty("cursor", "move", "important");
      style.setProperty("pointer-events", "none", "important");
      this.map.getCanvas().style.setProperty("cursor", "move", "important");
      this.map
        .getCanvas()
        .style.setProperty("pointer-events", "all", "important");
    },
    unsetCursorDrag() {
      document.documentElement.style.setProperty("cursor", "");
      document.documentElement.style.setProperty("pointer-events", "");
      this.map.getCanvas().style.cursor = "";
      this.map.getCanvas().style.pointerEvents = "";
    },
    removeLayers() {
      Object.values(LAYER_IDS).forEach(layerId => {
        this.map.removeLayer(layerId);
      });
      Object.values(SOURCE_IDS).forEach(sourceId => {
        this.map.removeSource(sourceId);
      });
    },
    removeEventListeners() {
      this.map.off(MAP_EVENTS.MOUSE_MOVE, this.mouseMoveMapHandler);
      this.map.off(MAP_EVENTS.MOUSE_UP, this.mouseUpMapHandler);
      this.map.off(
        LAYER_EVENTS[LAYER_IDS.AREA_SETTING_POINT_LAYER].MOUSE_MOVE,
        LAYER_IDS.AREA_SETTING_POINT_LAYER,
        this.mouseMoveLayerHandler
      );
      this.map.off(
        LAYER_EVENTS[LAYER_IDS.AREA_SETTING_POINT_LAYER].MOUSE_LEAVE,
        LAYER_IDS.AREA_SETTING_POINT_LAYER,
        this.mouseLeaveLayerHandler
      );

      this.map.off(
        LAYER_EVENTS[LAYER_IDS.AREA_SETTING_POINT_LAYER].MOUSE_DOWN,
        LAYER_IDS.AREA_SETTING_POINT_LAYER,
        this.mouseDownLayerHandler
      );
    },
  },
  created() {
    if (this.isReady) {
      if (!this.initialized) {
        this.initialize();
      }
    }
  },
  beforeDestroy() {
    this.removeEventListeners();
    this.removeLayers();
  },
};
</script>
